import { schema } from 'normalizr';
import { createAction } from 'redux-api-middleware';
import {
  buildQueryString,
  handleResponse,
  handleError,
  handleBundleSearchResponse,
} from '../utils';

export const FETCH_BUNDLE_REQUEST = 'FETCH_BUNDLE_REQUEST';
export const FETCH_BUNDLE_SUCCESS = 'FETCH_BUNDLE_SUCCESS';
export const FETCH_BUNDLE_FAILURE = 'FETCH_BUNDLE_FAILURE';
export const FETCH_BUNDLES_REQUEST = 'FETCH_BUNDLES_REQUEST';
export const FETCH_BUNDLES_SUCCESS = 'FETCH_BUNDLES_SUCCESS';
export const FETCH_BUNDLES_UPDATE_SUCCESS = 'FETCH_BUNDLES_UPDATE_SUCCESS';
export const FETCH_BUNDLES_FAILURE = 'FETCH_BUNDLES_FAILURE';
export const CREATE_BUNDLE_REQUEST = 'CREATE_BUNDLE_REQUEST';
export const CREATE_BUNDLE_SUCCESS = 'CREATE_BUNDLE_SUCCESS';
export const CREATE_BUNDLE_FAILURE = 'CREATE_BUNDLE_FAILURE';
export const EDIT_BUNDLE_REQUEST = 'EDIT_BUNDLE_REQUEST';
export const EDIT_BUNDLE_SUCCESS = 'EDIT_BUNDLE_SUCCESS';
export const EDIT_BUNDLE_FAILURE = 'EDIT_BUNDLE_FAILURE';
export const SEARCH_BUNDLE_REQUEST = 'SEARCH_BUNDLE_REQUEST';
export const SEARCH_BUNDLE_SUCCESS = 'SEARCH_BUNDLE_SUCCESS';
export const SEARCH_BUNDLE_FAILURE = 'SEARCH_BUNDLE_FAILURE';
export const FETCH_BUNDLEACTIVITY_REQUEST = 'FETCH_BUNDLEACTIVITY_REQUEST';
export const FETCH_BUNDLEACTIVITY_SUCCESS = 'FETCH_BUNDLEACTIVITY_REQUEST';
export const FETCH_BUNDLEACTIVITY_FAILURE = 'FETCH_BUNDLEACTIVITY_FAILURE';
export const FETCH_TRANSACTION_REQUEST = 'FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_FAILURE = 'FETCH_TRANSACTION_FAILURE';
export const DELETE_BUNDLE_REQUEST = 'DELETE_BUNDLE_REQUEST';
export const DELETE_BUNDLE_SUCCESS = 'DELETE_BUNDLE_SUCCESS';
export const DELETE_BUNDLE_FAILURE = 'DELETE_BUNDLE_FAILURE';
export const BUNDLE_PAGE_SIZE = 20;

export const bundlesSchema = new schema.Entity('bundles', {}, { idAttribute: 'bundleId' });

export const bundleVersionSchema = new schema.Entity('bundle', [], { idAttribute: '_id' });

const activity = new schema.Entity('activity', {}, { idAttribute: '_id' });
export const bundleActivitySchema = { activities: new schema.Array(activity) };

/**
 * Returns a redux api middleware action that makes a request for a bundle.
 *
 * @param {string} bundleId - Bundle Id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchBundle = bundleId =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles/${bundleId}`,
    method: 'GET',
    types: [
      { type: FETCH_BUNDLE_REQUEST },
      {
        type: FETCH_BUNDLE_SUCCESS,
        payload: (action, state, res) => handleResponse([bundleVersionSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_BUNDLE_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request for a collection of BUNDLES.
 *
 * @param {object} parameters - Query parameters.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchBundles = ({ start = 0, count = BUNDLE_PAGE_SIZE, owner = null } = {}) =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles?${buildQueryString({ start, count, owner })}`,
    method: 'GET',
    types: [
      { type: FETCH_BUNDLES_REQUEST },
      {
        type: start === 0 ? FETCH_BUNDLES_SUCCESS : FETCH_BUNDLES_UPDATE_SUCCESS,
        payload: (action, state, res) => handleResponse([bundlesSchema], action, state, res),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_BUNDLES_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request to create a bundle.
 *
 * @param {object} values - New bundle field values.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createBundle = values =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_BUNDLE_REQUEST },
      {
        type: CREATE_BUNDLE_SUCCESS,
        payload: (action, state, res) => handleResponse(bundlesSchema, action, state, res),
      },
      { type: CREATE_BUNDLE_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request to edit a bundle.
 *
 * @param {object} values - New bundle field values.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editBundle = values =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles`,
    method: 'PUT',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_BUNDLE_REQUEST },
      {
        type: EDIT_BUNDLE_SUCCESS,
        payload: (action, state, res) => handleResponse(bundlesSchema, action, state, res),
      },
      { type: EDIT_BUNDLE_FAILURE, payload: handleError },
    ],
  });

/**
 * Fetch bundle search results based on input value.
 *
 * @param {string} query - search input query.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const searchBundles = query =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles/search?q=${query}`,
    method: 'GET',
    types: [
      { type: SEARCH_BUNDLE_REQUEST },
      {
        type: SEARCH_BUNDLE_SUCCESS,
        payload: (action, state, res) => handleBundleSearchResponse(action, state, res),
        meta: { query },
      },
      { type: SEARCH_BUNDLE_FAILURE, payload: handleError },
    ],
  });

/**
 * Returns a redux api middleware action that makes a request to delete a bundle.
 *
 * @param {string} bundleId - Bundle Id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const deleteBundle = bundleId =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles/${bundleId}`,
    method: 'DELETE',
    body: JSON.stringify({ bundleId }),
    types: [
      { type: DELETE_BUNDLE_REQUEST },
      { type: DELETE_BUNDLE_SUCCESS },
      { type: DELETE_BUNDLE_FAILURE, payload: handleError },
    ],
  });

/**
 * Fetch bundle activity.
 *
 * @param {string} bundleId - Bundle Id.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchBundleActivity = bundleId =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles/activity/${bundleId}`,
    method: 'GET',
    types: [
      { type: FETCH_BUNDLEACTIVITY_REQUEST },
      {
        type: FETCH_BUNDLEACTIVITY_SUCCESS,
        payload: (action, state, res) => handleResponse(bundleActivitySchema, action, state, res),
        meta: { bundleId },
      },
      { type: FETCH_BUNDLEACTIVITY_FAILURE, payload: handleError },
    ],
  });

export const checkTransactions = (bundleId, bundleDate, revisedDate, dateType) =>
  createAction({
    endpoint: `${window.config.sapiUrl}/bundles/transactions/${bundleId}?${buildQueryString({
      bundleDate,
      revisedDate,
      dateType,
    })}`,
    method: 'GET',
    types: [
      { type: FETCH_TRANSACTION_REQUEST },
      {
        type: FETCH_TRANSACTION_SUCCESS,
      },
      { type: FETCH_TRANSACTION_FAILURE, payload: handleError },
    ],
  });
